import {Component, OnDestroy, OnInit} from '@angular/core';
import {NavController} from "@ionic/angular/lazy";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";
import {Platform} from '@ionic/angular/lazy';
import {App} from '@capacitor/app';
import {Subscription} from 'rxjs';
import {UserService} from "../../services/user.service";
import {UtilityService} from "../../services/utility.service";
import {LangService} from "../../services/lang.service";
import {StorageService} from "../../services/storage.service";
import {ToastService} from "../../services/toast.service";
import {AppSettingsService} from "../../services/app-settings.service";
import {Article} from "../../structure/types/article.class";
import {NotificationsService} from "../../services/notifications.service";
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {

    showHeader = false;
    showMobileHeader = true;

    private hiddenRoutes: string[] = [
        '/onboarding',
        '/splash',
        '/intro',
        '/2fa'
    ];
    private mobileHeaderHiddenRoutes: string[] = [
        '/guide/',
        '/risk-assesment/form/',
        '/short/',
    ];

    isDiscoverPage: boolean = false;
    isMoodLogPage: boolean = false;
    isProfilePage: boolean = false;
    isGuidesPage: boolean = false;
    isCrisisPage: boolean = false;
    crisisModeEnabled: boolean = true;
    unreadNotificationsCount: number = 0;
    needsAccountDetails: boolean = false;
    private _unreadSub: Subscription | undefined;

    /**
     * Badge count shown on the header bell. Includes the virtual
     * "complete your account details" prompt, which is not a stored
     * notification and so is not part of the backend unread count.
     */
    get notificationBadgeCount(): number {
        return this.unreadNotificationsCount + (this.needsAccountDetails ? 1 : 0);
    }

    user: any;

    private profileRoutes: string[] = [
        '/account',
        '/account/edit-account',
        '/account/change-password'
    ];

    private moodLogRoutes: string[] = [
        '/add-entry',
        '/calendar',
        '/not-logged-in'
    ];

    private discoverPageRoutes: string[] = [
        '/categories',
        '/home'
    ];

    private guidesRoutes: string[] = [
        '/guides',
        '/guide',
        '/search'
    ]

    private crisisRoutes: string[] = ['/crisis-mode']


    constructor(
        private platform: Platform,
        private router: Router,
        public userService: UserService,
        private navController: NavController,
        private utilityService: UtilityService,
        public lang: LangService,
        private storageService: StorageService,
        private toastService: ToastService,
        private appSettingsService: AppSettingsService,
        private notificationsService: NotificationsService,
    ) {
        this.appSettingsService.load();
        this.appSettingsService.crisisModeEnabled$.subscribe((enabled) => {
            this.crisisModeEnabled = enabled;
        });
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.checkRoute(event.urlAfterRedirects);
            }
        });

        this.userService.data.subscribe((userData) => {
            this.user = userData;
            this.needsAccountDetails = !!userData?.needs_account_details;
            this.checkRoute(this.router.url);

            // On (re)load, the account is restored asynchronously and emits here
            // before `isLoggedIn` is set, so refresh the badge as soon as the
            // account data actually arrives.
            if (userData && Object.keys(userData).length) {
                this.notificationsService.refreshUnreadCount();
            }
        });
    }

    ngOnInit() {
        this._unreadSub = this.notificationsService.unreadCount$.subscribe(count => {
            this.unreadNotificationsCount = count;
        });

    }

    ngOnDestroy() {
        this._unreadSub?.unsubscribe();
    }

    goToNotifications() {
        this.navController.navigateForward(['/notifications']);
    }

    /**
     * Check Current Route to set proper actions available
     * @param url
     * @private
     */
    private checkRoute(url: string) {
        if (url === "/") {
            this.showHeader = false;
            this.showMobileHeader = false;
            return;
        }

        this.isDiscoverPage = this.discoverPageRoutes.some(route => url.includes(route));
        this.isMoodLogPage = this.moodLogRoutes.some(route => url.includes(route));
        this.isGuidesPage = this.guidesRoutes.some(route => url.includes(route));
        this.isProfilePage = this.profileRoutes.some(route => url.includes(route));
        this.isCrisisPage = this.crisisRoutes.some(route => url.includes(route));

        this.showHeader = !this.hiddenRoutes.some(route => url.includes(route));
        this.showMobileHeader = !this.mobileHeaderHiddenRoutes.some(route => url.includes(route));
    }

    closeApp() {
        if(this.platform.is('ios')){
            this.userService.logout();
            this.navController.navigateRoot(['/home']);
            this.utilityService.openExternalLink("https://www.google.co.uk/");
        }else if (this.platform.is('hybrid')) {
            App.exitApp();
        } else {
            this.utilityService.openExternalLink("https://www.google.co.uk/");
        }
    }

    goToAccount() {
        let route = this.userService.isLoggedIn ? '/account' : '/login';
        this.navController.navigateRoot([route]);
    }

    goHome() {
        this.navController.navigateRoot(['/home']);
    }

    goToLogin() {
        this.navController.navigateRoot(['/login']);
    }

    async goToGuidesPage() {
        let token = await this.storageService.get('token');
        let moodRoute = '/guides-not-logged-in';
        if (this.userService.isLoggedIn && token !== null) {
            moodRoute = '/guides'
        }
        await this.navController.navigateRoot([moodRoute]);
    }

    async goToExplore() {
        await this.navController.navigateRoot(['/search']);
    }

    async goToMoodLog() {
        let token = await this.storageService.get('token');
        let moodRoute = '/calendar-not-logged-in';
        if (this.userService.isLoggedIn && token !== null) {
            moodRoute = '/calendar'
        }
        await this.navController.navigateRoot([moodRoute]);
    }

    async openLiveChat() {
        await this.utilityService.openExternalLink('https://livechat.victimsupport.org.uk/newchat/chat.aspx?domain=www.mysupportspace.org.uk')
    }

    /**
     * Crisis mode is available to everyone, guests included.
     */
    async goToCrisis() {
        await this.navController.navigateRoot(['/crisis-mode']);
    }

    goToFavorite() {
        let route = this.userService.isLoggedIn ? '/account/bookmarks' : '/login';
        this.navController.navigateRoot([route]);
    }

    async logout() {
        await this.userService.logout();

        this.storageService.remove('token');
        this.userService.isLoggedIn = false;
        this.userService.getAccount();
        await this.toastService.toast(
            this.lang.data['logged_out_message'],
            'success',
            false,
            '/home'
        );
    }
}
